import React, { useEffect, useState } from "react";
import { IconButton, Box, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Cancel, CheckCircle } from "@mui/icons-material";
import { BiErrorCircle } from "react-icons/bi";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  LoginSubmitBtn,
  LoginContainer,
  LoginFormContent,
  LoginFormWrapper,
  LoginWrapper,
  Title,
  SubTitle,
  InputLogin,
  LoginForm,
  LoginFieldset,
  InputWrapper,
  InputIconLeft,
  InputIconRight,
  BrandPanel,
} from "./LoginFields.styled";

import menuImg from "../../assets/imgs/logoSaudePlenaMoinhos.svg";
import { ReactComponent as ChangePasswordSuccess } from "../../assets/imgs/passwordChangeSuccess.svg";

import { useAppDispatch } from "../../index";
import * as loginActions from "../../store/actions/login";
import useResponsiveInfo from "../../hooks/useResponsiveInfo";
import SuccessModal from "../successModal/SuccessModal";
import User from "../../models/User";
import { AppStorage } from "../../storage/AppStorage";
import { Auth } from "../../core/http/service/Auth";
import AuthDTO from "../../models/auth/AuthDTO";
import { UserDataTypes } from "../../store/actions/login";
import { mountUserDataPostLogin } from "../../helpers/LoginHelperFunctions";
import { cleanMask } from "../../helpers/HelperFunctions";

function ChangePassword() {
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [failedFirstRule, setFailedFirstRule] = useState(true);
  const [failedSecondRule, setFailedSecondRule] = useState(true);
  const [failedThirdRule, setFailedThirdRule] = useState(true);
  const [failedFourthRule, setFailedFourthRule] = useState(true);
  const [failedPassword, setFailedPassword] = useState(true);

  const [showModalPasswordChanged, setShowModalPasswordChanged] = useState(false);
  const [searchParams] = useSearchParams();
  const [urlCode, setUrlCode] = useState<any>(null);
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const isPrimeiroAcesso = state !== null ? state.isPrimeiroAcesso : false;

  const [modalExperation, setModalExperation] = useState(false);

  const dispatch = useAppDispatch();
  const { isMobile } = useResponsiveInfo();

  useEffect(() => {
    const code = searchParams.get("code")?.replaceAll(" ", "+");
    if (state === null && code === undefined) {
      navigate("/");
    }
  }, [state, searchParams, navigate]);

  useEffect(() => {
    const code = searchParams.get("code")?.replaceAll(" ", "+");
    if (code !== null && code?.trim() !== "") {
      setUrlCode(encodeURIComponent(code ?? ""));
    } else {
      navigate("/");
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    setFailedFirstRule(newPassword.length < 8);
    setFailedSecondRule(!(/[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword)));
    setFailedThirdRule(!/[0-9]/.test(newPassword));
    setFailedFourthRule(!/[^\w\s]/.test(newPassword));
    setFailedPassword(!(newPassword === confirmNewPassword && newPassword.length > 0));
  }, [newPassword, confirmNewPassword]);

  const validatePasswordEquality = () => {
    return newPassword.trim() === confirmNewPassword.trim();
  };

  const PasswordRuleIcon = ({ error }: any) => {
    return error ? (
      <Cancel color="error" sx={{ fontSize: 18, mr: 1, mt: "2px" }} />
    ) : (
      <CheckCircle color="success" sx={{ fontSize: 18, mr: 1, mt: "2px" }} />
    );
  };

  async function _onSubmitAlterPassword(
    e: React.FormEvent<HTMLFormElement>,
    newPass: string,
    dispatchFn: any
  ) {
    e.preventDefault();

    try {
      const userCPF = searchParams.get("user");
      const user = isPrimeiroAcesso
        ? await AppStorage.getCookieByKey("USER")
        : userCPF;
      const email = await AppStorage.getCookieByKey("emailRetrievePassword");
      const code = isPrimeiroAcesso
        ? await AppStorage.getAccessToken()
        : urlCode;

      if (validatePasswordEquality()) {
        await dispatchFn(
          loginActions.changePassword(
            new User().fromJSON({
              user: cleanMask(user?.replaceAll('"', "") ?? ""),
              email: email?.replaceAll('"', ""),
            }),
            newPass,
            code ?? ""
          )
        );
        setShowModalPasswordChanged(true);
      }
    } catch (error: any) {
      setModalExperation(true);
    }
  }

  const handleToCallbackResetPassword = () => {
    navigate("/");
  };

  const firstLoginAutoTokenUpdate = async () => {
    const user = await AppStorage.getCookieByKey("USER");
    if (isPrimeiroAcesso) {
      const auth = new Auth();
      const data = new AuthDTO().fromJSON({
        user: user?.replaceAll('"', ""),
        password: newPassword,
      });
      try {
        const loginResponse: any = await auth.postAuth(data);
        const userData: UserDataTypes = mountUserDataPostLogin(
          user,
          newPassword,
          loginResponse,
          false,
          []
        );

        await dispatch(loginActions.login(userData));
        navigate("/");
      } catch (error: any) {}
    } else {
      navigate("/");
    }
  };

  const failedSomeRule =
    failedFirstRule ||
    failedSecondRule ||
    failedThirdRule ||
    failedFourthRule ||
    failedPassword;
  const disabledButton = failedSomeRule || !validatePasswordEquality();

  return (
    <LoginWrapper>
      <LoginContainer>
        <LoginFormWrapper>
          <LoginFormContent>
            <Title>Crie uma nova senha</Title>
            <SubTitle>
              Sua nova senha deve ser forte e diferente das senhas utilizadas anteriormente.
            </SubTitle>

            <LoginForm
              onSubmit={(e) =>
                !failedSomeRule &&
                _onSubmitAlterPassword(e, newPassword, dispatch)
              }
            >
              <LoginFieldset>
                <InputWrapper>
                  <InputIconLeft>
                    <LockOutlinedIcon fontSize="small" />
                  </InputIconLeft>
                  <InputLogin
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Nova senha"
                  />
                  <InputIconRight>
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ color: "#6b7280" }}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputIconRight>
                </InputWrapper>

                <InputWrapper>
                  <InputIconLeft>
                    <LockOutlinedIcon fontSize="small" />
                  </InputIconLeft>
                  <InputLogin
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Confirme a nova senha"
                  />
                  <InputIconRight>
                    <IconButton
                      size="small"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      sx={{ color: "#6b7280" }}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputIconRight>
                </InputWrapper>

                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: "10px",
                    bgcolor: "#F9FAFB",
                    border: "1px solid #E5E7EB",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.8,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PasswordRuleIcon error={failedFirstRule} />
                    <Typography variant="caption" sx={{ color: "#374151", fontWeight: 500 }}>
                      Pelo menos 8 caracteres
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PasswordRuleIcon error={failedSecondRule} />
                    <Typography variant="caption" sx={{ color: "#374151", fontWeight: 500 }}>
                      Letras maiúsculas e minúsculas
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PasswordRuleIcon error={failedThirdRule} />
                    <Typography variant="caption" sx={{ color: "#374151", fontWeight: 500 }}>
                      Dígitos numéricos (0-9)
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PasswordRuleIcon error={failedFourthRule} />
                    <Typography variant="caption" sx={{ color: "#374151", fontWeight: 500 }}>
                      Caracteres especiais (!@#$)
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PasswordRuleIcon error={failedPassword} />
                    <Typography variant="caption" sx={{ color: "#374151", fontWeight: 500 }}>
                      As senhas coincidem
                    </Typography>
                  </Box>
                </Box>

                <LoginSubmitBtn
                  style={{
                    marginTop: 12,
                    opacity: disabledButton ? 0.5 : 1,
                    cursor: disabledButton ? "default" : "pointer",
                  }}
                  disabled={disabledButton}
                  type="submit"
                >
                  Alterar Senha
                </LoginSubmitBtn>
              </LoginFieldset>
            </LoginForm>
          </LoginFormContent>
        </LoginFormWrapper>

        {!isMobile && (
          <BrandPanel>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
                textAlign: "center",
              }}
            >
              <img
                src={menuImg}
                style={{ width: "280px", maxWidth: "100%", height: "auto" }}
                alt="Logo Saúde Plena Moinhos"
              />
              <Box
                sx={{
                  width: 40,
                  height: 3,
                  bgcolor: "#FF8310",
                  borderRadius: 2,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: "#4B5563",
                  fontWeight: 600,
                  fontSize: "14px",
                  maxWidth: 260,
                  lineHeight: 1.5,
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Segurança e Privacidade em Saúde Digital
              </Typography>
            </Box>
          </BrandPanel>
        )}
      </LoginContainer>

      <SuccessModal
        Icon={() => <ChangePasswordSuccess />}
        modalMsg="Senha alterada com sucesso!"
        visible={showModalPasswordChanged}
        onVoltar={async () => {
          await firstLoginAutoTokenUpdate();
        }}
      />

      <SuccessModal
        Icon={() => <BiErrorCircle size={140} color="#ffffff" />}
        modalMsg="Excedeu o tempo limite para a troca da senha, tente novamente!"
        visible={modalExperation}
        onVoltar={async () => {
          await handleToCallbackResetPassword();
        }}
      />
    </LoginWrapper>
  );
}

export default ChangePassword;
